import {
    type ForwardedRef,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { GA4EventName, trackPDPAccordions } from '@jsmdg/tracking';
import { Accordion, type Tab } from '@jsmdg/yoshi';
import {
    AccordionContext,
    type AccordionContextValue,
    type AccordionItemId,
} from '@jsmdg/yoshi/dist/components/Accordion/AccordionContext';
import { ProductType } from '../../../shared/enums';
import { isExperience } from '../../../shared/product-safeguards';
import { type Product } from '../../../shared/types';
import { dispatchEvent } from '../../helper/EventUtils';
import { useHistoryPopstate } from '../../hooks/useHistoryPopstate';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { eventTypes } from '../../types';
import { type ProductDetailContext } from '../../types/types';
import { buildVoucherPartnerContent } from '../ProductInfo/ProductInfoContentBuilder';
import { ReviewsHighlight } from '../ReviewsHighlight';
import { Venues } from '../Venues';
import { AccordionContent } from './AccordionContent';
import { CaptionTitle } from './AccordionIcon';
import { AccordionTitle } from './AccordionTitle';
import { setExternalReviewScript } from './setExternalReviewScript';
import styles from './AccordionContainer.module.scss';
import contentStyles from './AccordionContent.module.scss';

const SHOW_REVIEWS_HIGHLIGHT_LIMIT = 5;

const AccordionSidePanel = loadable(async () => import('./AccordionSidePanel'));

interface AccordionContainerProps {
    readonly product: Product;
    readonly accordions: Tab[];
    readonly isMobile: boolean;
}

type AccordionsForwardRef = {
    setActiveItemId: AccordionContextValue['setActiveItemId'];
    activeItemId: AccordionContextValue['activeItemId'];
};

const messages = defineMessages({
    header: {
        defaultMessage: 'Alles auf einen Blick',
    },
});

const AccordionTitleWrapper = forwardRef(
    (
        { caption }: { readonly caption: string },
        ref: ForwardedRef<AccordionsForwardRef>,
    ): JSX.Element => {
        const { activeItemId, setActiveItemId } = useContext(AccordionContext);

        useImperativeHandle(
            ref,
            () => ({
                setActiveItemId,
                activeItemId,
            }),
            [setActiveItemId, activeItemId],
        );

        return <AccordionTitle caption={caption} />;
    },
);

AccordionTitleWrapper.displayName = AccordionTitleWrapper.name;

const trackAccordionsAfterYield = async (title: string, isOpen: boolean): Promise<void> => {
    await window.yieldToMainThread();
    trackPDPAccordions(title, isOpen ? GA4EventName.OpenAccordion : GA4EventName.CloseAccordion);
};

const AccordionContainer = ({
    accordions,
    isMobile,
    product,
}: AccordionContainerProps): JSX.Element => {
    const { isMydays, reviewPartnerConfiguration } = useFragmentContext<ProductDetailContext>();
    const intl = useIntl();
    const [activeAccordion, setActiveAccordion] = useState<Tab | null | undefined>(null);
    const reviewsAccordionRef = useRef<AccordionsForwardRef | null>(null);
    const [isReviewsAccordionOpen, setIsReviewsAccordionOpen] = useState(false);

    const voucherPartnerInformation = useMemo(() => buildVoucherPartnerContent(product), [product]);

    const isPdfBoxExperience =
        isExperience(product) &&
        product.productGroup?.length === 2 &&
        product.productGroup?.some(group => group.type === ProductType.BOX);

    const canRenderLocationsMap =
        isExperience(product) &&
        product.locations?.length > 0 &&
        !product.voucherPartnerInformation?.iframeUrl &&
        !isPdfBoxExperience;

    const isModalOpen = isMobile && !!activeAccordion?.caption;
    const scrollPosition = useScrollPosition({ saveScrollPosition: isMobile });

    const closeModal = useCallback(() => {
        setActiveAccordion(null);

        if (isMobile) {
            setTimeout(() => {
                window.scrollTo(0, scrollPosition);
            }, 10);
        }
    }, [isMobile, scrollPosition]);

    const onOpenAccordionItem = useCallback(
        async (accordion?: Tab, isOpen = false): Promise<void> => {
            if (accordion) {
                const title =
                    accordion.caption === CaptionTitle.MoreDetails && !isMydays
                        ? CaptionTitle.UsefulInfo
                        : accordion.caption;

                await trackAccordionsAfterYield(title, isOpen);
            }

            return undefined;
        },

        [isMydays],
    );

    const onCollapsibleToggle = async (itemId: AccordionItemId, isOpen: boolean): Promise<void> => {
        const accordion = accordions.find(({ caption }: Tab) => caption === (itemId as string));
        await onOpenAccordionItem(accordion, isOpen);

        if (itemId === CaptionTitle.Review) {
            if (isReviewsAccordionOpen) {
                reviewsAccordionRef.current?.setActiveItemId(null);
                setIsReviewsAccordionOpen(false);
            } else {
                setIsReviewsAccordionOpen(true);
            }
        }
    };

    useHistoryPopstate({ closeModal, isModalOpen });

    useEffect(() => {
        const loadExternalReviewScript = async (): Promise<void> => {
            const hasReviewsAccordion = accordions.some(
                ({ caption }: Tab) => caption === CaptionTitle.Review,
            );

            if (hasReviewsAccordion) {
                await window.yieldToMainThread();
                setExternalReviewScript(reviewPartnerConfiguration.scriptUrl);
            }
        };

        function switchToReviewsTab(): void {
            setIsReviewsAccordionOpen(true);

            if (reviewsAccordionRef.current?.activeItemId === CaptionTitle.Review) {
                document
                    .querySelector(`#${CaptionTitle.Review}`)
                    ?.scrollIntoView({ behavior: 'smooth' });
                return;
            }

            reviewsAccordionRef.current?.setActiveItemId(CaptionTitle.Review);

            setTimeout(() => {
                const element = document.querySelector(`#${CaptionTitle.Review}`);
                element?.scrollIntoView({ behavior: 'smooth' });
            }, 400);
        }

        (async () => loadExternalReviewScript())();

        document.addEventListener(eventTypes.BUYBOX_RATINGS_CLICK, switchToReviewsTab);

        return function cleanup() {
            document.removeEventListener(eventTypes.BUYBOX_RATINGS_CLICK, switchToReviewsTab);
        };
    }, [accordions, onOpenAccordionItem, reviewPartnerConfiguration.scriptUrl]);

    function createReviewsAccordionRef(ref: AccordionsForwardRef | null, caption: string): void {
        if (caption === CaptionTitle.Review) {
            reviewsAccordionRef.current = ref;
        }
    }

    const getActiveItemIds = (): string | null => {
        if (accordions.length <= 1) {
            return null;
        }

        return accordions[0].caption;
    };

    const handleShowReviewById = ({ id }: { id: number }): void => {
        if (isReviewsAccordionOpen) {
            document
                .querySelector(`[data-review-id="${id}"]`)
                ?.scrollIntoView({ behavior: 'smooth' });
        } else {
            reviewsAccordionRef.current?.setActiveItemId(CaptionTitle.Review);
            setIsReviewsAccordionOpen(true);
            setTimeout(() => {
                const element = document.querySelector(`[data-review-id="${id}"]`);
                element?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
        }
    };

    return (
        <>
            <h2 className={classNames('theme-text-headline pt-4x mb-1-5x', styles.headline)}>
                <span>{intl.formatMessage(messages.header)}</span>
            </h2>

            {accordions.length === 1 && (
                <>
                    <AccordionTitleWrapper caption={accordions[0].caption} />
                    <div className={classNames('mb-5x', contentStyles.content)}>
                        {accordions[0].content}
                    </div>
                </>
            )}

            {accordions.length !== 1 && (
                <Accordion
                    autoClose={isMobile}
                    onSelect={onCollapsibleToggle}
                    activeItemsIds={getActiveItemIds()}
                >
                    {accordions.map(({ caption, content }, index) => (
                        <Accordion.Item
                            key={caption}
                            id={caption}
                            title={
                                <AccordionTitleWrapper
                                    caption={caption}
                                    ref={ref => createReviewsAccordionRef(ref, caption)}
                                />
                            }
                            className="pb-2x"
                            contentClasses={classNames('pl-0-5x ml-4x', styles.accordionContent, {
                                'pt-2x': !caption && index > 0,
                            })}
                        >
                            <AccordionContent content={content} />
                        </Accordion.Item>
                    ))}
                </Accordion>
            )}
            {product.rating?.count >= SHOW_REVIEWS_HIGHLIGHT_LIMIT && (
                <ReviewsHighlight
                    handleShowAll={() => dispatchEvent(eventTypes.RATINGS_CLICK)}
                    openReviewLink={id => handleShowReviewById({ id })}
                />
            )}
            {canRenderLocationsMap && <Venues product={product} />}
            {voucherPartnerInformation.length > 0 && (
                <div className="mb-4x">
                    <div className={classNames('mb-3x', styles.caption)}>
                        {voucherPartnerInformation[0].caption}
                    </div>
                    {voucherPartnerInformation[0].content}
                </div>
            )}

            {isModalOpen && (
                <AccordionSidePanel
                    isSidePanelOpen={isModalOpen}
                    closeSidePanel={closeModal}
                    accordion={activeAccordion}
                />
            )}
        </>
    );
};

export { AccordionContainer };
